import React, { useState } from "react";
import "./index.scss";

export const ForgotPasswordPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Step 1: Enter email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Veuillez entrer votre adresse email");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://api.almasecret.msit-demo.fr/users/forgot-password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setCurrentStep(2);
      } else {
        setError(data.message || "Erreur lors de l'envoi de l'email");
      }
    } catch (error) {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Enter OTP code
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      setError("Veuillez entrer le code OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("https://api.almasecret.msit-demo.fr/users/verify-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, otpCode }),
      });

      const data = await response.json();

      if (response.ok) {
        setCurrentStep(3);
      } else {
        setError(data.message || "Code OTP invalide");
      }
    } catch (error) {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Enter new password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (newPassword.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://api.almasecret.msit-demo.fr/users/reset-password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, otpCode, newPassword }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Show success popup
        setShowSuccessPopup(true);
      } else {
        setError(
          data.message || "Erreur lors de la réinitialisation du mot de passe"
        );
      }
    } catch (error) {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="forgot-step">
      <h1 className="forgot-title">MOT DE PASSE OUBLIÉ</h1>
      <p className="forgot-description">
        Entrez votre adresse email pour recevoir un code de vérification
      </p>

      <form onSubmit={handleEmailSubmit} className="forgot-form">
        <div className="forgot-form-group">
          <div className="forgot-input-wrapper">
            <span className="forgot-input-icon">✉</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="EMAIL*"
              required
            />
          </div>
        </div>

        {error && <div className="forgot-error">{error}</div>}

        <button
          type="submit"
          className={`forgot-button ${isLoading ? "loading" : ""}`}
          disabled={isLoading}>
          {isLoading ? "ENVOI EN COURS..." : "ENVOYER LE CODE"}
        </button>
      </form>

      <div className="forgot-back-link">
        <a href="/login-page" className="forgot-back-text">
          ← Retour à la connexion
        </a>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="forgot-step">
      <h1 className="forgot-title">CODE DE VÉRIFICATION</h1>
      <p className="forgot-description">
        Entrez le code à 6 chiffres envoyé à {email}
      </p>

      <form onSubmit={handleOtpSubmit} className="forgot-form">
        <div className="forgot-form-group">
          <div className="forgot-input-wrapper">
            <span className="forgot-input-icon">🔐</span>
            <input
              type="text"
              value={otpCode}
              onChange={(e) =>
                setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="CODE OTP*"
              maxLength="6"
              required
            />
          </div>
        </div>

        {error && <div className="forgot-error">{error}</div>}

        <button
          type="submit"
          className={`forgot-button ${isLoading ? "loading" : ""}`}
          disabled={isLoading}>
          {isLoading ? "VÉRIFICATION..." : "VÉRIFIER LE CODE"}
        </button>
      </form>

      <div className="forgot-back-link">
        <button onClick={() => setCurrentStep(1)} className="forgot-back-text">
          ← Changer d'email
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="forgot-step">
      <h1 className="forgot-title">NOUVEAU MOT DE PASSE</h1>
      <p className="forgot-description">Entrez votre nouveau mot de passe</p>

      <form onSubmit={handlePasswordSubmit} className="forgot-form">
        <div className="forgot-form-group">
          <div className="forgot-input-wrapper">
            <span className="forgot-input-icon">***</span>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="NOUVEAU MOT DE PASSE*"
              required
            />
          </div>
        </div>

        <div className="forgot-form-group">
          <div className="forgot-input-wrapper">
            <span className="forgot-input-icon">***</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="CONFIRMER LE MOT DE PASSE*"
              required
            />
          </div>
        </div>

        {error && <div className="forgot-error">{error}</div>}

        <button
          type="submit"
          className={`forgot-button ${isLoading ? "loading" : ""}`}
          disabled={isLoading}>
          {isLoading ? "RÉINITIALISATION..." : "RÉINITIALISER LE MOT DE PASSE"}
        </button>
      </form>

      <div className="forgot-back-link">
        <button onClick={() => setCurrentStep(2)} className="forgot-back-text">
          ← Retour au code
        </button>
      </div>
    </div>
  );

  return (
    <div className="forgot-page">
      <div className="forgot-container">
        <div className="forgot-form-section">
          <div className="forgot-form-container">
            <div className="forgot-logo-section">
              <div className="forgot-brand-logo">Alma Secret.</div>
              <div className="forgot-brand-subtitle">
                NATURAL & THERMAL COSMETICS
              </div>
            </div>

            <div className="forgot-progress">
              <div
                className={`forgot-progress-step ${
                  currentStep >= 1 ? "active" : ""
                }`}>
                <span className="forgot-progress-number">1</span>
                <span className="forgot-progress-label">Email</span>
              </div>
              <div
                className={`forgot-progress-line ${
                  currentStep >= 2 ? "active" : ""
                }`}></div>
              <div
                className={`forgot-progress-step ${
                  currentStep >= 2 ? "active" : ""
                }`}>
                <span className="forgot-progress-number">2</span>
                <span className="forgot-progress-label">Code</span>
              </div>
              <div
                className={`forgot-progress-line ${
                  currentStep >= 3 ? "active" : ""
                }`}></div>
              <div
                className={`forgot-progress-step ${
                  currentStep >= 3 ? "active" : ""
                }`}>
                <span className="forgot-progress-number">3</span>
                <span className="forgot-progress-label">Mot de passe</span>
              </div>
            </div>

            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="forgot-success-popup">
          <div className="forgot-success-content">
            <div className="forgot-success-icon">✓</div>
            <h2 className="forgot-success-title">
              Mot de passe mis à jour avec succès!
            </h2>
            <p className="forgot-success-message">
              Votre mot de passe a été réinitialisé avec succès. Vous pouvez
              maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            <button
              className="forgot-success-button"
              onClick={() => (window.location.href = "/login-page")}>
              Se connecter
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
